import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Crown, CreditCard, Lock, Ticket } from "@phosphor-icons/react";
import { toast } from "sonner";
import { UsersTab } from "@/components/admin/UsersTab";
import { AccountsTab } from "@/components/admin/AccountsTab";
import { PLAN_LABEL } from "@/components/admin/adminUtils";
import { usePrivateMode } from "@/lib/privateMode";
import { OverviewTab } from "@/components/admin/OverviewTab";
import { ActivityTab } from "@/components/admin/ActivityTab";
import { DeletedTab } from "@/components/admin/DeletedTab";
import { OfferDialog } from "@/components/admin/OfferDialog";
import { StripeDialog } from "@/components/admin/StripeDialog";
import { PinDialog } from "@/components/admin/PinDialog";
import { DeleteUserDialog } from "@/components/admin/DeleteUserDialog";
import { RestoreDialog } from "@/components/admin/RestoreDialog";

export default function Admin() {
  const { user, loading } = useAuth();
  const priv = usePrivateMode();
  const [tab, setTab] = useState("overview");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [deleted, setDeleted] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [pinSet, setPinSet] = useState(false);
  const [stripeCfg, setStripeCfg] = useState(null);
  const [offer, setOffer] = useState(null);

  const [stripeOpen, setStripeOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch {
      toast.error("Could not load users");
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadPinStatus = useCallback(async () => {
    try {
      const res = await api.get("/admin/security/pin");
      setPinSet(!!res.data.pin_set);
    } catch (err) { console.error("Failed to load PIN status", err); }
  }, []);

  const loadStripe = useCallback(async () => {
    try {
      const res = await api.get("/admin/security/stripe");
      setStripeCfg(res.data);
    } catch (err) { console.error("Failed to load Stripe config", err); }
  }, []);

  const loadOffer = useCallback(async () => {
    try {
      const res = await api.get("/admin/offer");
      setOffer(res.data);
    } catch (err) { console.error("Failed to load lifetime offer", err); }
  }, []);

  const loadDeleted = useCallback(async () => {
    setLoadingDeleted(true);
    try {
      const res = await api.get("/admin/deleted-accounts");
      setDeleted(res.data);
    } catch {
      toast.error("Could not load deleted accounts");
    } finally {
      setLoadingDeleted(false);
    }
  }, []);

  useEffect(() => { if (user?.is_super_admin) { load(); loadPinStatus(); loadStripe(); loadOffer(); } }, [load, loadPinStatus, loadStripe, loadOffer, user]);
  useEffect(() => { if (user?.is_super_admin && tab === "deleted") loadDeleted(); }, [tab, user, loadDeleted]);

  const setPlan = async (u, orgId, plan) => {
    if (!orgId) { toast.error("This user has no workspace to grant a tier to."); return; }
    try {
      await api.post(`/admin/orgs/${orgId}/plan`, { plan });
      toast.success(`${u.name || u.email} → ${PLAN_LABEL[plan] || plan}`);
      if (plan === "internal" || u.plan === "internal") { load(); return; }
      setUsers((prev) => prev.map((x) => {
        if (x.user_id !== u.user_id) return x;
        const isPrimary = orgId === x.primary_org_id;
        return {
          ...x,
          plan: isPrimary ? plan : x.plan,
          subscription_status: isPrimary && plan !== "personal" ? "active" : x.subscription_status,
          canceled_at: isPrimary && plan !== "personal" ? null : x.canceled_at,
          workspaces: x.workspaces.map((w) => w.org_id === orgId ? { ...w, plan } : w),
        };
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to grant tier");
    }
  };

  const removeInternal = async (u, orgId) => {
    if (!window.confirm(`Remove Unlimited (Internal) from ${u.name || u.email}? The workspace returns to its underlying plan (${PLAN_LABEL[u.base_plan] || u.base_plan}) and its limits.`)) return;
    try {
      await api.delete(`/admin/orgs/${orgId}/internal-unlimited`);
      toast.success("Unlimited (Internal) removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };

  const openDelete = (u) => {
    if (!pinSet) {
      toast.error("Set a delete PIN first (Security).");
      setPinOpen(true);
      return;
    }
    setDeleteTarget(u);
  };

  const handleDeleted = (userId) => {
    setUsers((prev) => prev.filter((x) => x.user_id !== userId));
    setDeleteTarget(null);
  };

  const handleRestored = (userId) => {
    setDeleted((prev) => prev.filter((x) => x.user_id !== userId));
    setRestoreTarget(null);
    load();
  };

  if (!loading && user && !user.is_super_admin) return <Navigate to="/dashboard" replace />;

  const tierCounts = users.reduce((acc, u) => { if (u.plan) acc[u.plan] = (acc[u.plan] || 0) + 1; return acc; }, {});

  return (
    <AppShell>
      <div className="px-6 py-8 max-w-6xl w-full mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Crown size={22} weight="fill" className="text-amber-400" />
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Admin</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!priv && <Button
              data-testid="admin-offer-settings-btn"
              variant="outline"
              onClick={() => { loadOffer(); setOfferOpen(true); }}
              className="bg-black border-zinc-800 hover:border-zinc-600 gap-2"
            >
              <Ticket size={15} className={offer?.active ? "text-amber-400" : "text-zinc-500"} />
              Lifetime offer
              {offer?.active && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />}
            </Button>}
            <Button
              data-testid="admin-stripe-settings-btn"
              variant="outline"
              onClick={() => { loadStripe(); setStripeOpen(true); }}
              className="bg-black border-zinc-800 hover:border-zinc-600 gap-2"
            >
              <CreditCard size={15} className={stripeCfg?.api_key_set ? "text-emerald-400" : "text-amber-400"} />
              Stripe keys
            </Button>
            <Button
              data-testid="admin-security-pin-btn"
              variant="outline"
              onClick={() => setPinOpen(true)}
              className="bg-black border-zinc-800 hover:border-zinc-600 gap-2"
            >
              <Lock size={15} className={pinSet ? "text-emerald-400" : "text-amber-400"} />
              {pinSet ? "Change delete PIN" : "Set delete PIN"}
            </Button>
          </div>
        </div>
        <p className="text-sm text-zinc-400 mb-6">
          {users.length} users · Free {tierCounts.personal || 0} · Solo {tierCounts.solo || 0} · Group {tierCounts.group || 0} · Team {tierCounts.smallbiz || 0}{tierCounts.internal ? ` · Unlimited ${tierCounts.internal}` : ""}
          {(tierCounts.starter || tierCounts.pro || tierCounts.business || tierCounts.enterprise) ? (
            <span className="text-zinc-600"> · legacy {(tierCounts.starter || 0) + (tierCounts.pro || 0) + (tierCounts.business || 0) + (tierCounts.enterprise || 0)}</span>
          ) : null}
        </p>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-5 border-b border-white/10 overflow-x-auto">
          <button
            data-testid="admin-tab-overview"
            onClick={() => setTab("overview")}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === "overview" ? "border-blue-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            Overview
          </button>
          <button
            data-testid="admin-tab-users"
            onClick={() => setTab("users")}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === "users" ? "border-blue-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            Users
          </button>
          <button
            data-testid="admin-tab-accounts"
            onClick={() => setTab("accounts")}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === "accounts" ? "border-blue-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            Accounts
          </button>
          <button
            data-testid="admin-tab-activity"
            onClick={() => setTab("activity")}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === "activity" ? "border-blue-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            Activity
          </button>
          <button
            data-testid="admin-tab-deleted"
            onClick={() => setTab("deleted")}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors flex items-center gap-1.5 ${tab === "deleted" ? "border-blue-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
          >
            Recently Deleted
            {deleted.length > 0 && <span className="text-[10px] rounded-full bg-zinc-800 px-1.5 py-0.5 text-zinc-300">{deleted.length}</span>}
          </button>
        </div>

        {tab === "overview" && (
          <OverviewTab />
        )}

        {tab === "accounts" && <AccountsTab />}

        {tab === "activity" && (
          <ActivityTab />
        )}

        {tab === "users" && (
          <UsersTab users={users} loading={loadingUsers} onSetPlan={setPlan} onRemoveInternal={removeInternal} onDelete={openDelete} />
        )}

        {tab === "deleted" && (
          <DeletedTab deleted={deleted} loading={loadingDeleted} onRestore={setRestoreTarget} />
        )}
      </div>

      <OfferDialog open={offerOpen} onOpenChange={setOfferOpen} offer={offer} onSaved={setOffer} />
      <StripeDialog open={stripeOpen} onOpenChange={setStripeOpen} stripeCfg={stripeCfg} onSaved={loadStripe} />
      <PinDialog open={pinOpen} onOpenChange={setPinOpen} pinSet={pinSet} onSaved={() => setPinSet(true)} />
      <DeleteUserDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={handleDeleted} />
      <RestoreDialog target={restoreTarget} onClose={() => setRestoreTarget(null)} onRestored={handleRestored} />
    </AppShell>
  );
}
